import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row,Tooltip as ReactTooltip, OverlayTrigger, Spinner } from 'react-bootstrap';
import styled from 'styled-components';
import { StyledCardBodyOverflow, StyledCardHeader } from '../../../../components/dashboard/attentionPoints/styles';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FormControl, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, SvgIcon, Tooltip } from '@mui/material';
import SparklineChart from './SparklineChart';
import { GroupParticipationReportRow } from '../../../../interfaces/insights.interface';
import { IndicatorIconGreen, IndicatorIconRed } from '../../../../components/IndicatorIcons/IndicadorIcons';
import { Ball, getClassificationText } from '../../../../components/coloredBall';
import { RangeParams } from '@viniarruda/react-month-range-picker/types/MonthRangePicker/types';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../../i18n/useAppLocale';
import { getMonthName } from '../../../../i18n/analytics';
import ExportMenuButton from '../../../../components/exportMenuButton';
import { searchGroups } from '../../../../services/group/group';
import { useAuth } from '../../../../context/AuthContext';
import { useToast } from '../../../../context/Toast';
import {
  copyGroupParticipationToClipboard,
  exportGroupParticipationCsv,
} from './exportGroupParticipationCsv';

const GroupChartHeader = styled(StyledCardHeader)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    flex-direction: column;
  }

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const ContainerPaginationDivision = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ContainerArrows = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    align-items: flex-start;
  }

  @media (max-width: 420px) {
    gap: 2em;
  }
`;

type SortOrder = 'alpha-asc' | 'alpha-desc' | 'last-month-asc' | 'last-month-desc' | 'avg-asc' | 'avg-desc';

type GroupParticipationReportProps = {
  title?: string;
  rows?: GroupParticipationReportRow[];
  loading?: boolean;
  range?: RangeParams;
  hasRepresentationFilter?: boolean;
};

const pageSize = 5;

const formatPercent = (value: number | null, locale: string) => {
  if (value === null || Number.isNaN(value)) {
    return '-';
  }

  return `${value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const formatPercentChange = (value: number | null, locale: string) => {
  if (value === null || Number.isNaN(value)) {
    return '- -';
  }

  // Label utilizada para quando o valor do início e do fim do período são os mesmos
  if (value === 0) {
    return '- -';
  }

  const absoluteValue = Math.abs(value).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const signal = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${signal}${absoluteValue}%`;
};

const getLastMonthLabel = (range: RangeParams | undefined, locale: Parameters<typeof getMonthName>[1], t: (key: string) => string) => {
  if (!range?.endMonth || !range?.endYear) {
    return t('groups.lastMonth');
  }

  const monthLabel = getMonthName(range.endMonth, locale, true);
  const yearLabel = String(range.endYear).slice(-2);

  return `${t('groups.lastMonth')} ${monthLabel}/${yearLabel}`;
};

const GroupParticipationReport: React.FC<GroupParticipationReportProps> = ({
  title,
  rows = [],
  loading = false,
  range,
  hasRepresentationFilter = false
}) => {
  const { t } = useTranslation('insights');
  const appLocale = useAppLocale();
  const { user } = useAuth();
  const { showToast } = useToast();
  const classificationLabels = { critical: t('classificationCritical'), bad: t('classificationBad'), fair: t('classificationFair'), good: t('classificationGood'), excellent: t('classificationExcellent') };
  const [sortOrder, setSortOrder] = useState<SortOrder>('alpha-asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setCurrentPage(0);
  }, [rows]);

  const collator = useMemo(() => new Intl.Collator(appLocale, { sensitivity: 'base', numeric: true }), [appLocale]);

  const sortedRows = useMemo(() => {
    const decoratedRows = rows.map((row, index) => ({ row, originalIndex: index }));

    const compareNullableNumberValues = (left: number | null, right: number | null, direction: 'asc' | 'desc') => {
      if (left === null && right === null) {
        return 0;
      }

      if (left === null) {
        return 1;
      }

      if (right === null) {
        return -1;
      }

      return direction === 'asc' ? left - right : right - left;
    };

    decoratedRows.sort((left, right) => {
      if (sortOrder === 'alpha-asc') {
        const result = collator.compare(left.row.groupName, right.row.groupName);
        return result !== 0 ? result : left.originalIndex - right.originalIndex;
      }

      if (sortOrder === 'alpha-desc') {
        const result = collator.compare(right.row.groupName, left.row.groupName);
        return result !== 0 ? result : left.originalIndex - right.originalIndex;
      }

      if (sortOrder === 'last-month-asc') {
        const result = compareNullableNumberValues(left.row.lastMonthAdherence, right.row.lastMonthAdherence, 'asc');
        if (result !== 0) {
          return result;
        }
        return collator.compare(left.row.groupName, right.row.groupName) || (left.originalIndex - right.originalIndex);
      }

      if (sortOrder === 'last-month-desc') {
        const result = compareNullableNumberValues(left.row.lastMonthAdherence, right.row.lastMonthAdherence, 'desc');
        if (result !== 0) {
          return result;
        }
        return collator.compare(left.row.groupName, right.row.groupName) || (left.originalIndex - right.originalIndex);
      }

      if (sortOrder === 'avg-asc') {
        const result = compareNullableNumberValues(left.row.average, right.row.average, 'asc');
        if (result !== 0) {
          return result;
        }
        return collator.compare(left.row.groupName, right.row.groupName) || (left.originalIndex - right.originalIndex);
      }

      const result = compareNullableNumberValues(left.row.average, right.row.average, 'desc');
      if (result !== 0) {
        return result;
      }
      return collator.compare(left.row.groupName, right.row.groupName) || (left.originalIndex - right.originalIndex);
    });

    return decoratedRows.map(({ row }) => row);
  }, [rows, sortOrder, collator]);

  const totalItems = sortedRows.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;
  const hasRows = totalItems > 0;
  const isInitialLoading = loading && !hasRows;
  const lastMonthLabel = getLastMonthLabel(range, appLocale, t);
  const reportTitle = title || t('groups.title');

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages - 1) {
      setCurrentPage(0);
    }
  }, [totalPages, currentPage]);

  const visibleRows = useMemo(() => {
    const startIndex = currentPage * pageSize;
    return sortedRows.slice(startIndex, startIndex + pageSize);
  }, [currentPage, sortedRows]);

  const displayStart = totalItems === 0 ? 0 : currentPage * pageSize + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min((currentPage + 1) * pageSize, totalItems);

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as SortOrder);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => setCurrentPage((previous) => Math.max(previous - 1, 0));
  const goToNextPage = () => setCurrentPage((previous) => Math.min(previous + 1, Math.max(totalPages - 1, 0)));

  const handleExport = async () => {
    const companyId = user.company_visited?.id;

    if (!companyId) {
      showToast(String(t('groups.exportError')), 'error');
      return;
    }

    try {
      setIsExporting(true);
      const groupsResult = await searchGroups({
        company_id: companyId,
        groupsStatuses: [1],
        detailedMembersData: true,
      });

      exportGroupParticipationCsv({
        rows: sortedRows,
        groups: groupsResult.data,
        locale: appLocale,
        companyName: user.company_visited?.name_company,
        periodMonth: range?.endMonth,
        periodYear: range?.endYear,
        headers: {
          responsibleUsers: String(t('groups.exportResponsibleUsers')),
          grouping: String(t('groups.exportGrouping')),
          groupAdherence: String(t('groups.exportGroupAdherence')),
        },
        fileNamePrefix: String(t('groups.exportFileName')),
      });
    } catch (error) {
      console.error('Erro ao exportar a participação por agrupamentos:', error);
      showToast(String(t('groups.exportError')), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopy = async () => {
    const companyId = user.company_visited?.id;

    if (!companyId) {
      showToast(String(t('groups.copyError')), 'error');
      return;
    }

    try {
      setIsExporting(true);
      const groupsResult = await searchGroups({
        company_id: companyId,
        groupsStatuses: [1],
        detailedMembersData: true,
      });

      await copyGroupParticipationToClipboard({
        rows: sortedRows,
        groups: groupsResult.data,
        locale: appLocale,
        periodMonth: range?.endMonth,
        periodYear: range?.endYear,
        headers: {
          responsibleUsers: String(t('groups.exportResponsibleUsers')),
          grouping: String(t('groups.exportGrouping')),
          groupAdherence: String(t('groups.exportGroupAdherence')),
        },
      });
      showToast(String(t('groups.copySuccess')), 'success');
    } catch (error) {
      console.error('Erro ao copiar a participação por agrupamentos:', error);
      showToast(String(t('groups.copyError')), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const controlsDisabled = loading || totalItems === 0;
  const statusText = loading
      ? hasRows
      ? t('groups.showingLoading', { start: displayStart, end: displayEnd, total: totalItems })
      : t('groups.loadingMore')
    : totalItems > 0
      ? t('groups.showing', { start: displayStart, end: displayEnd, total: totalItems })
      : t('groups.empty');

  return (
    <Row className='pb-2'>
      <Col>
        <StyledCardBodyOverflow style={{ background: '#FFF', color: '#3C4043', overflowX: 'auto' }}>
          <GroupChartHeader style={{ padding: '1rem 1.25rem', borderBottom: '2px solid #e7e8e9' }}>
            <h5 className='pb-0 mb-0'>{reportTitle}</h5>
            <ContainerPaginationDivision>
              <PaginationContainer>
                <span style={{ marginRight: '1rem', color: '#5f6368', fontSize: '0.875rem' }}>
                  {statusText}
                </span>

                <ContainerArrows>
                  <OverlayTrigger placement='top' overlay={<ReactTooltip id='group-participation-prev-page'>{t('groups.previous')}</ReactTooltip>}>
                    <button
                      onClick={goToPreviousPage}
                      disabled={controlsDisabled || currentPage === 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: controlsDisabled || currentPage === 0 ? 'not-allowed' : 'pointer',
                        padding: '0.25rem 0em',
                        color: controlsDisabled || currentPage === 0 ? '#adb5bd' : '#202124'
                      }}
                    >
                      <BsArrowLeft />
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger placement='top' overlay={<ReactTooltip id='group-participation-next-page'>{t('groups.next')}</ReactTooltip>}>
                    <button
                      onClick={goToNextPage}
                      disabled={controlsDisabled || currentPage >= totalPages - 1}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: controlsDisabled || currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                        padding: '0.25rem 0em',
                        color: controlsDisabled || currentPage >= totalPages - 1 ? '#adb5bd' : '#202124'
                      }}
                    >
                      <BsArrowRight />
                    </button>
                  </OverlayTrigger>
                </ContainerArrows>
              </PaginationContainer>
              <FormControl
                sx={{ minWidth: 220, border: '1px solid #e7e8e9', borderRadius: '6px', color: '#3C4043', fontSize: '16px' }}
                size='small'
              >
                <Select
                  value={sortOrder}
                  onChange={handleSortChange}
                  displayEmpty
                  disabled={loading || totalItems <= 1}
                   inputProps={{ 'aria-label': String(t('groups.sortLabel')) }}
                >
                  <MenuItem value='alpha-asc'>{t('groups.alphaAsc')}</MenuItem>
                  <MenuItem value='alpha-desc'>{t('groups.alphaDesc')}</MenuItem>
                  <MenuItem value='last-month-asc'>{`${lastMonthLabel}: ${t('groups.worstBest')}`}</MenuItem>
                  <MenuItem value='last-month-desc'>{`${lastMonthLabel}: ${t('groups.bestWorst')}`}</MenuItem>
                  <MenuItem value='avg-asc'>{`${t('groups.average')}: ${t('groups.worstBest')}`}</MenuItem>
                  <MenuItem value='avg-desc'>{`${t('groups.average')}: ${t('groups.bestWorst')}`}</MenuItem>
                </Select>
              </FormControl>
              <ExportMenuButton
                tooltip={String(t('groups.exportTooltip'))}
                exportLabel={String(t('groups.exportCsv'))}
                copyLabel={String(t('groups.copyClipboard'))}
                onExport={handleExport}
                onCopy={handleCopy}
                disabled={loading || isExporting || totalItems === 0}
              />
            </ContainerPaginationDivision>
          </GroupChartHeader>
          <Card.Body style={{ padding: 0 }}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '0.75rem', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
              <Table size='small' aria-label={String(t('groups.tableLabel'))} sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ minHeight: 64 }}>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align='center'>{t('groups.group')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align='center'>{lastMonthLabel}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align='center'>{t('groups.period')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align='center'>{t('groups.average')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 1.5 }} align='center'>{t('groups.variation')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isInitialLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 5 }}>
                        <div className='d-flex align-items-center justify-content-center' style={{ minHeight: '140px' }}>
                          <Spinner animation='border' style={{ color: '#c9c9c9' }} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : totalItems === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                        {t('groups.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {visibleRows.map((row) => (
                        <TableRow sx={{ fontSize: '16px'}} key={row.groupId}>
                          <TableCell align='center' sx={{ py: 0.05 }}>
                            {row.groupName}
                            {hasRepresentationFilter ? ` (${t('groups.filterSuffix')})` : ''}
                          </TableCell>
                          <TableCell align='center' sx={{ py: 0.05 }}>
                            <Tooltip title={getClassificationText(row.lastMonthAdherence, classificationLabels)} placement='top' arrow enterTouchDelay={0} leaveTouchDelay={2000}>
                              <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'.4em'}}>
                                {formatPercent(row.lastMonthAdherence, appLocale)}
                                <Ball percentage={row.lastMonthAdherence} />
                              </div>
                            </Tooltip>
                          </TableCell>
                          <TableCell align='center' sx={{ py: 0.05, verticalAlign: 'middle', minWidth: 240 }}>
                            <SparklineChart data={row.periodAdherence} />
                          </TableCell>
                          <TableCell align='center' sx={{ py: 0.05 }}>
                            <Tooltip title={getClassificationText(row.average, classificationLabels)} placement='top' arrow enterTouchDelay={0} leaveTouchDelay={2000}>
                              <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'.4em'}}>
                                {formatPercent(row.average, appLocale)}
                                <Ball percentage={row.average} />
                              </div>
                            </Tooltip>
                          </TableCell>
                          <TableCell align='center' sx={{ py: 0.05 }}>
                           
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '183px' }}>
                              {row.variation != null && row.variation > 0 && <IndicatorIconGreen size='small'/>}
                              {row.variation != null && row.variation < 0 && <IndicatorIconRed size='small'/>}
                              <span style={{ marginLeft: '4px' }}>{formatPercentChange(row.variation, appLocale)}</span>
                            </div>
                          
                          </TableCell>
                        </TableRow>
                      ))}
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={5} align='center' sx={{ py: 2 }}>
                            <span style={{ color: '#5f6368', fontSize: '0.875rem' }}>{t('groups.loadingMore')}</span>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card.Body>
        </StyledCardBodyOverflow>
      </Col>
    </Row>
  );
};

export default GroupParticipationReport;

